import { Button, Card, Grid, TextField, Typography } from "@mui/material";
import { Formula } from "protos/almanac/almanac";
import { FORMULA_FIELD_CONSTRAINTS } from "portal/utils/almanac";
import { numberOrEmptyString } from "portal/utils/forms";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import CopyIcon from "@mui/icons-material/ContentCopyOutlined";
import PasteIcon from "@mui/icons-material/ContentPasteOutlined";
import React, { Fragment, FunctionComponent } from "react";

const getFormula = (
  t: TFunction
): Array<{
  label: string;
  description: string;
  key: keyof Formula;
}> => [
  {
    label: t("components.almanac.formulas.multiplier.label"),
    key: "multiplier",
    description: t(
      "views.categoryTemplates.shootTime.advancedFields.multiplier.description"
    ),
  },
  {
    label: t("components.almanac.formulas.offset.label"),
    key: "offset",
    description: t(
      "views.categoryTemplates.shootTime.advancedFields.offset.description"
    ),
  },
  {
    label: t("components.almanac.formulas.exponent.label"),
    key: "exponent",
    description: t(
      "views.categoryTemplates.shootTime.advancedFields.exponent.description"
    ),
  },
  {
    label: t("components.almanac.formulas.maxTime.label"),
    key: "maxTime",
    description: t(
      "views.categoryTemplates.shootTime.advancedFields.maxTime.description"
    ),
  },
  {
    label: t("components.almanac.formulas.fineTuneMultiplier.label"),
    key: "fineTuneMultiplier",
    description: t(
      "components.almanac.formulas.fineTuneMultiplier.description"
    ),
  },
  {
    label: t("components.almanac.formulas.fineTuneMultiplierVal.label"),
    key: "fineTuneMultiplierVal",
    description: t(
      "views.categoryTemplates.shootTime.advancedFields.fineTuneMultiplierVal.description"
    ),
  },
];

interface Props {
  copiedFormula: Formula | undefined;
  formula: Formula;
  onChange: (newFormula: Formula) => void;
  setCopiedFormula: (formula: Formula | undefined) => void;
  title: string;
  readOnly?: boolean;
}

export const FormulaCard: FunctionComponent<Props> = ({
  copiedFormula,
  formula,
  onChange,
  setCopiedFormula,
  title,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  return (
    <Card className="p-8 my-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 w-full gap-4">
        <Typography variant="h6">{title}</Typography>
        <div className="flex items-center gap-2">
          <Button
            variant="contained"
            color="secondary"
            startIcon={<CopyIcon />}
            onClick={() => setCopiedFormula(formula)}
          >
            {t("components.almanac.formulas.copyFormula")}
          </Button>
          {copiedFormula && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<PasteIcon />}
              disabled={readOnly}
              onClick={() => {
                onChange({
                  ...formula,
                  ...(readOnly
                    ? {
                        fineTuneMultiplier: copiedFormula.fineTuneMultiplier,
                      }
                    : copiedFormula),
                });
              }}
            >
              {t("components.almanac.formulas.pasteFormula")}
            </Button>
          )}
        </div>
      </div>
      {/* Reference */}
      <div className="flex flex-col items-center text-sm my-4">
        <strong>
          {t("components.almanac.formulas.laserTime")} ={" "}
          {/* don't try to localize formula */}
          {/* eslint-disable i18next/no-literal-string */}
          <span className="font-mono">
            F × (A × r<sup>e</sup> + b)
          </span>
        </strong>
        <span className="font-mono">F = 1 + ((FI - 5) × FM)</span>
        {/* eslint-enable i18next/no-literal-string */}
      </div>
      {/* Form */}
      <Grid
        container
        rowSpacing={2}
        columnSpacing={2}
        className="w-full items-center justify-center"
      >
        {getFormula(t).map(({ label, description, key }) => {
          const { min, max, step } = FORMULA_FIELD_CONSTRAINTS[key];
          return (
            <Fragment key={key}>
              <Grid
                className="font-bold"
                size={{
                  xs: 6,
                  md: 1.5,
                }}
              >
                {label}
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 1.5,
                }}
              >
                <TextField
                  size="small"
                  variant="outlined"
                  disabled={readOnly && key !== "fineTuneMultiplier"}
                  type="number"
                  value={formula[key]}
                  className="w-full text-center"
                  onChange={(event) =>
                    onChange({
                      ...formula,
                      [key]: numberOrEmptyString(event.target.value),
                    })
                  }
                  slotProps={{
                    htmlInput: { min, max, step },
                  }}
                />
              </Grid>
              <Grid
                className="text-sm"
                size={{
                  xs: 12,
                  md: 8.5,
                }}
              >
                {description}
              </Grid>
            </Fragment>
          );
        })}
      </Grid>
    </Card>
  );
};
